import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/transfers")({
  component: TransfersPage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ConfirmDialog } from "@/components/crud/ConfirmDialog/ConfirmDialog";
import {
  useFetchTransfers,
  useTransferMutations,
  type TransferOrdering,
} from "@/hooks/api/inventory/useStockOps";
import {
  useFetchWarehouses,
  useFetchItems,
  useFetchUnits,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDebounce } from "@/hooks/use-debounce";
import { TransferTable } from "@/components/inventory/tables/TransferTable";
import { TransferFormSheet } from "@/components/inventory/forms/TransferFormSheet";
import { TransferDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import type {
  StockTransfer,
  StockTransferPayload,
  TransferStatus,
} from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

const STATUS_OPTIONS = [
  { label: "Initiated", value: "INITIATED" },
  { label: "In Transit", value: "IN_TRANSIT" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Cancelled", value: "CANCELLED" },
] satisfies { label: string; value: TransferStatus }[];

function TransfersPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  // StockTransferViewSet has no required_permission_map — list/retrieve
  // fall to read_permission (inventory.stock.view), every write action
  // (create/update/dispatch/complete/cancel) to write_permission
  // (inventory.transfer.manage).
  const canView = can(capabilities, "inventory.stock.view");
  const canManage = can(capabilities, "inventory.transfer.manage");

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    TransferStatus | undefined
  >(undefined);
  const [fromWarehouseFilter, setFromWarehouseFilter] = useState<
    string | undefined
  >(undefined);
  const [toWarehouseFilter, setToWarehouseFilter] = useState<
    string | undefined
  >(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchInput, 500);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [viewing, setViewing] = useState<StockTransfer | null>(null);
  const [editing, setEditing] = useState<StockTransfer | null>(null);
  const [dispatchTarget, setDispatchTarget] = useState<StockTransfer | null>(null);
  const [completeTarget, setCompleteTarget] = useState<StockTransfer | null>(null);
  const [cancelTarget, setCancelTarget] = useState<StockTransfer | null>(null);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        transfer_number: SortDirection;
        status: SortDirection;
        transfer_date: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      TransferOrdering
    >({
      transfer_number: null,
      status: null,
      transfer_date: null,
      created_at: null,
      updated_at: null,
    });

  const transfersQuery = useFetchTransfers(
    {
      page_size: pageSize,
      page,
      search: debouncedSearch || undefined,
      ordering: getApiOrdering(),
      status: statusFilter,
      from_warehouse: fromWarehouseFilter ? Number(fromWarehouseFilter) : undefined,
      to_warehouse: toWarehouseFilter ? Number(toWarehouseFilter) : undefined,
    },
    { enabled: canView },
  );
  const transfers = transfersQuery.data?.results ?? [];
  const count = transfersQuery.data?.count ?? 0;

  const { createTransfer, updateTransfer, dispatchTransfer, completeTransfer, cancelTransfer } =
    useTransferMutations();
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const itemsQuery = useFetchItems(ALL_ACTIVE_PARAMS);
  const items = itemsQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS);
  const units = unitsQuery.data?.results ?? [];

  const isMutating =
    createTransfer.isPending ||
    updateTransfer.isPending ||
    dispatchTransfer.isPending ||
    completeTransfer.isPending ||
    cancelTransfer.isPending;
  const isLoading = (canView && transfersQuery.isPending) || isMutating;
  const isFetching = (canView && transfersQuery.isFetching) || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    ordering,
    statusFilter,
    fromWarehouseFilter,
    toWarehouseFilter,
    setPage,
  ]);

  const resetFilters = () => {
    setOrdering({
      transfer_number: null,
      status: null,
      transfer_date: null,
      created_at: null,
      updated_at: null,
    });
    setSearchInput("");
    resetDebounce();
    setStatusFilter(undefined);
    setFromWarehouseFilter(undefined);
    setToWarehouseFilter(undefined);
    setPage(1);
  };

  const handleSubmit = async (data: StockTransferPayload) => {
    if (editing) {
      await updateTransfer.mutateAsync({ id: editing.id, payload: data });
      toast.success("Transfer updated");
      setEditing(null);
    } else {
      await createTransfer.mutateAsync(data);
      toast.success("Transfer created");
      setIsSheetOpen(false);
    }
  };

  const handleDispatch = (t: StockTransfer) => setDispatchTarget(t);

  const handleComplete = (t: StockTransfer) => setCompleteTarget(t);

  const handleDispatchConfirm = async () => {
    if (!dispatchTarget) return;
    try {
      await dispatchTransfer.mutateAsync({ id: dispatchTarget.id });
      toast.success("Transfer dispatched");
      setDispatchTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleCompleteConfirm = async () => {
    if (!completeTarget) return;
    try {
      await completeTransfer.mutateAsync({ id: completeTarget.id });
      toast.success("Transfer completed");
      setCompleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await cancelTransfer.mutateAsync({ id: cancelTarget.id });
      toast.success("Transfer cancelled");
      setCancelTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Stock Transfers"
          description="Move stock between warehouses"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? () => setIsSheetOpen(true) : undefined}
          addLabel="New Transfer"
          isLoading={isFetching}
        />

        {canView && (
        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            placeholder="Search TRF number..."
            value={searchInput}
            onChange={setSearchInput}
          />
          <SelectFilter
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          />
          <SelectFilter
            placeholder="From Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={fromWarehouseFilter}
            onChange={(v) => setFromWarehouseFilter(v)}
          />
          <SelectFilter
            placeholder="To Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={toWarehouseFilter}
            onChange={(v) => setToWarehouseFilter(v)}
          />
        </FiltersPanel>
        )}

        {canView && (
        <TransferTable
          transfers={transfers}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          onNameClick={(t) =>
            navigate({
              to: "/admin/inventory/transfers/$id",
              params: { id: String(t.id) },
            })
          }
          onView={(t) => setViewing(t)}
          onEdit={(t) => setEditing(t)}
          onDispatch={handleDispatch}
          onComplete={handleComplete}
          onCancel={(t) => setCancelTarget(t)}
          canManage={canManage}
        />
        )}
        {canView && (
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        )}

        <TransferDetailSheet
          isOpen={!!viewing}
          onClose={() => setViewing(null)}
          transfer={viewing}
        />

        <TransferFormSheet
          isOpen={isSheetOpen || !!editing}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          warehouses={warehouses}
          items={items}
          units={units}
          isLoading={editing ? updateTransfer.isPending : createTransfer.isPending}
          editing={editing}
        />

        <DeleteConfirmDialog
          isOpen={!!cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleCancelConfirm}
          title="Cancel Transfer"
          description="Cancel this stock transfer? Stock will not be moved."
          itemName={String(cancelTarget?.id ?? "")}
          isLoading={cancelTransfer.isPending}
          confirmLabel="Cancel Transfer"
          loadingLabel="Cancelling..."
          destructive
        />

        <ConfirmDialog
          isOpen={!!dispatchTarget}
          onClose={() => setDispatchTarget(null)}
          onConfirm={handleDispatchConfirm}
          isLoading={dispatchTransfer.isPending}
          title="Dispatch Transfer"
          description={`Dispatch transfer "${dispatchTarget?.transfer_number}"? Stock will be marked as in transit.`}
          confirmLabel="Dispatch"
          loadingLabel="Dispatching..."
        />

        <ConfirmDialog
          isOpen={!!completeTarget}
          onClose={() => setCompleteTarget(null)}
          onConfirm={handleCompleteConfirm}
          isLoading={completeTransfer.isPending}
          title="Complete Transfer"
          description={`Complete transfer "${completeTarget?.transfer_number}"? Stock will be added to the destination warehouse.`}
          confirmLabel="Complete"
          loadingLabel="Completing..."
        />
      </div>
    </SidebarInset>
  );
}
